<section class="trip-search-page">
  <form [formGroup]="searchForm" class="search-panel" (ngSubmit)="searchTrips()">
    <label>
      Free text
      <input formControlName="q" type="search" placeholder="Operator, trip, vehicle, stop address">
    </label>
    <label>
      Route or stop
      <input formControlName="routeOrStop" type="search" placeholder="CBD Express, Main Station">
    </label>
    <label>
      Date
      <input formControlName="date" type="date">
    </label>
    <label>
      Time
      <input formControlName="time" type="time">
    </label>
    <label>
      Operator name
      <input formControlName="operatorName" type="search" placeholder="Operator name">
    </label>
    <label>
      Operator
      <select formControlName="operatorId">
        <option [ngValue]="null">Any operator</option>
        <option *ngFor="let operator of operators" [ngValue]="getOperatorId(operator)">
          {{ getOperatorName(operator) }}
        </option>
      </select>
    </label>
    <div class="search-actions">
      <button class="btn btn-primary" type="submit" [disabled]="isSearching">
        <i class="material-icons">search</i>
        {{ isSearching ? 'Searching...' : 'Search' }}
      </button>
      <button class="secondary-action" type="button" (click)="clearFilters()">Clear</button>
    </div>
  </form>

  <div class="alert error" *ngIf="searchError">
    <i class="material-icons">error</i>
    <span>{{ searchError }}</span>
  </div>
  <div class="alert" role="status" *ngIf="featureStateLoaded && !passengerBookingEnabled">
    <i class="material-icons">info</i>
    <span>Online booking is currently unavailable. You can still view available trips.</span>
  </div>

  <section class="booking-lookup">
    <div>
      <p class="eyebrow">Booking recovery</p>
      <h3>Find an existing booking</h3>
    </div>
    <form [formGroup]="lookupForm" (ngSubmit)="lookupBooking()" class="lookup-form">
      <input formControlName="bookingReference" type="text" placeholder="Booking reference">
      <button class="secondary-action" type="submit">Lookup</button>
    </form>
    <span class="error" *ngIf="lookupError">{{ lookupError }}</span>
    <div class="lookup-result" *ngIf="lookupResult">
      <strong>{{ getConfirmationReference(lookupResult) }}</strong>
      <span>{{ lookupResult.passengerName || 'Passenger' }}</span>
      <a *ngIf="getConfirmationTicketUrl(lookupResult)" [href]="getConfirmationTicketUrl(lookupResult)" target="_blank" rel="noopener">Open e-ticket</a>
    </div>
  </section>

  <div class="booking-confirmation" *ngIf="confirmation">
    <div>
      <p class="eyebrow">Booking confirmed</p>
      <h2>{{ getConfirmationReference(confirmation) }}</h2>
      <p>Payment status: {{ confirmation.paymentStatus || 'PayAtTicketOffice' }}</p>
    </div>
    <dl>
      <div>
        <dt>Trip</dt>
        <dd>{{ getConfirmationTripName(confirmation) }}</dd>
      </div>
      <div>
        <dt>Departure</dt>
        <dd>{{ getConfirmationDeparture(confirmation) | date:'medium' }}</dd>
      </div>
      <div>
        <dt>Seats</dt>
        <dd>{{ getConfirmationSeats(confirmation).length ? getConfirmationSeats(confirmation).join(', ') : confirmation.seatsBooked }}</dd>
      </div>
      <div>
        <dt>Passenger</dt>
        <dd>{{ confirmation.passengerName }}</dd>
      </div>
      <div>
        <dt>Contact</dt>
        <dd>{{ confirmation.passengerEmail || confirmation.passengerPhone }}</dd>
      </div>
      <div>
        <dt>Payment instruction</dt>
        <dd>Pay at the ticket office before travel. No online card payment is active yet.</dd>
      </div>
    </dl>
    <a class="primary-action" *ngIf="getConfirmationTicketUrl(confirmation)" [href]="getConfirmationTicketUrl(confirmation)" target="_blank" rel="noopener">
      <i class="material-icons">confirmation_number</i>
      Open e-ticket
    </a>
    <p class="notification-note">Email delivery is preferred when available. SMS delivery is a future notification channel; phone is captured for recovery and future messaging.</p>
  </div>

  <div class="search-layout">
    <section class="results-panel">
      <div class="panel-header">
        <div>
          <p class="eyebrow">Trip results</p>
          <h3>{{ trips.length }} found</h3>
        </div>
      </div>

      <div class="loading-state" *ngIf="isSearching">
        <i class="material-icons">sync</i>
        <span>Searching trips...</span>
      </div>

      <div class="results-list" *ngIf="!isSearching && trips.length; else emptyResults">
        <article class="trip-card" *ngFor="let trip of trips" [class.selected]="selectedTrip && getTripId(selectedTrip) === getTripId(trip)">
          <div class="trip-card-header">
            <span class="status-pill">{{ getTripOperatorName(trip) }}</span>
            <strong>{{ getSeatsAvailable(trip) }} seats</strong>
          </div>
          <h3>{{ getTripRouteName(trip) }}</h3>
          <p>{{ getKeyStops(trip) }}</p>
          <div class="trip-metrics">
            <span><i class="material-icons">schedule</i>{{ getTripStart(trip) | date:'short' }}</span>
            <span><i class="material-icons">flag</i>{{ getTripEnd(trip) | date:'short' }}</span>
            <span><i class="material-icons">directions_bus</i>{{ getTripVehicleLabel(trip) }}</span>
          </div>
          <div class="trip-actions">
            <button class="secondary-action" type="button" (click)="viewTrip(trip)">Details</button>
            <button class="primary-action" type="button" *ngIf="passengerBookingEnabled" (click)="viewTrip(trip)">Book</button>
          </div>
        </article>
      </div>

      <ng-template #emptyResults>
        <div class="empty-state" *ngIf="hasSearched && !isSearching">
          <i class="material-icons">search_off</i>
          <h3>No trips found</h3>
          <p>Try a wider route or stop search, another date, or remove the operator filter.</p>
        </div>
      </ng-template>
    </section>

    <aside class="detail-panel">
      <div class="empty-state" *ngIf="!selectedTrip && !isLoadingDetail && !detailError">
        <i class="material-icons">event_seat</i>
        <h3>Select a trip</h3>
        <p>Trip detail, stops, seat map, and booking form will appear here.</p>
      </div>

      <div class="loading-state" *ngIf="isLoadingDetail">
        <i class="material-icons">sync</i>
        <span>Loading trip detail...</span>
      </div>

      <div class="alert error" *ngIf="detailError">
        <i class="material-icons">error</i>
        <span>{{ detailError }}</span>
      </div>

      <ng-container *ngIf="selectedTrip && !isLoadingDetail">
        <div class="detail-header">
          <div>
            <p class="eyebrow">{{ getTripOperatorName(selectedTrip) }}</p>
            <h2>{{ getTripRouteName(selectedTrip) }}</h2>
            <p>{{ getTripStart(selectedTrip) | date:'medium' }} - {{ getTripEnd(selectedTrip) | date:'shortTime' }}</p>
          </div>
          <span class="status-pill">{{ getSeatsAvailable(selectedTrip) }} available</span>
        </div>

        <section class="detail-section" *ngIf="passengerBookingEnabled">
          <h3>Route stops</h3>
          <ol class="stop-list" *ngIf="getOrderedStops(selectedTrip).length; else noStops">
            <li *ngFor="let stop of getOrderedStops(selectedTrip)">
              <span>{{ getStopOrder(stop) || '-' }}</span>
              <strong>{{ getStopName(stop) }}</strong>
            </li>
          </ol>
          <ng-template #noStops>
            <p class="muted-copy">Stops are not available for this trip yet.</p>
          </ng-template>
        </section>

        <section class="detail-section" *ngIf="passengerBookingEnabled">
          <h3>Seat map</h3>
          <p class="muted-copy">Booked seats are disabled. Unusual vehicle layouts are shown as a simple numbered grid.</p>
          <div class="seat-map" [class.large]="getTripCapacity(selectedTrip) > 40">
            <button type="button"
                    *ngFor="let seat of getSeatNumbers()"
                    [disabled]="isSeatBooked(seat)"
                    [class.booked]="isSeatBooked(seat)"
                    [class.selected]="isSeatSelected(seat)"
                    (click)="toggleSeat(seat)">
              {{ seat }}
            </button>
          </div>
          <p class="seat-message" *ngIf="seatSelectionMessage">{{ seatSelectionMessage }}</p>
        </section>

        <section class="detail-section">
          <h3>Book seats</h3>
          <form [formGroup]="bookingForm" (ngSubmit)="bookSelectedTrip()" class="booking-form">
            <label>
              Passenger name *
              <input formControlName="passengerName" type="text" placeholder="Passenger Name">
            </label>
            <label>
              Email
              <input formControlName="passengerEmail" type="email" placeholder="passenger@example.com">
              <small>Email is preferred for e-ticket delivery.</small>
            </label>
            <label>
              Phone
              <input formControlName="passengerPhone" type="tel" placeholder="+27000000000">
              <small>SMS is not live yet, but phone can be captured now.</small>
            </label>
            <label>
              Seats booked *
              <input formControlName="seatsBooked" type="number" min="1" [max]="getSeatsAvailable(selectedTrip)">
            </label>
            <div class="payment-note">
              <i class="material-icons">payments</i>
              Payment: PayAtTicketOffice
            </div>
            <div class="alert error" *ngIf="bookingError">
              <i class="material-icons">error</i>
              <span>{{ bookingError }}</span>
            </div>
            <button class="primary-action" type="submit" [disabled]="isBooking">
              <i class="material-icons">event_seat</i>
              {{ isBooking ? 'Booking...' : 'Reserve seats' }}
            </button>
          </form>
        </section>
      </ng-container>
    </aside>
  </div>
</section>
